'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface Service {
  id: string;
  name: string;
  dependsOn: string[]; // IDs of services this depends on
  protocol: 'http' | 'grpc' | 'messaging' | 'database';
  criticalityLevel: 'critical' | 'high' | 'medium' | 'low';
}

interface DependencyIssue {
  type: 'circular' | 'spof' | 'deep-chain';
  services: string[];
  severity: 'critical' | 'high' | 'medium';
  description: string;
  recommendation: string;
}

export default function MicroservicesDependencyMapperPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'microservices-dependency-mapper',
    toolCategory: 'analyzer',
  });

  // Services (free tier limited to 10)
  const [services, setServices] = useState<Service[]>([
    { id: '1', name: 'API Gateway', dependsOn: [], protocol: 'http', criticalityLevel: 'critical' },
    { id: '2', name: 'Auth Service', dependsOn: ['3'], protocol: 'http', criticalityLevel: 'critical' },
    { id: '3', name: 'User DB', dependsOn: [], protocol: 'database', criticalityLevel: 'critical' },
  ]);

  // Analysis results
  const analysis = useMemo(() => {
    const validServices = services.filter(s => s.name.trim());

    // Build dependency graph
    const graph = new Map<string, Set<string>>();
    const reverseGraph = new Map<string, Set<string>>(); // who depends on this service

    validServices.forEach(service => {
      graph.set(service.id, new Set(service.dependsOn));
      if (!reverseGraph.has(service.id)) {
        reverseGraph.set(service.id, new Set());
      }
      service.dependsOn.forEach(depId => {
        if (!reverseGraph.has(depId)) {
          reverseGraph.set(depId, new Set());
        }
        reverseGraph.get(depId)!.add(service.id);
      });
    });

    // Detect circular dependencies
    const circularDeps: DependencyIssue[] = [];
    const visited = new Set<string>();
    const recStack = new Set<string>();

    const detectCycle = (serviceId: string, path: string[]): boolean => {
      if (recStack.has(serviceId)) {
        const cycleStart = path.indexOf(serviceId);
        const cycle = path.slice(cycleStart);
        cycle.push(serviceId);

        const serviceNames = cycle.map(id =>
          validServices.find(s => s.id === id)?.name || id
        );

        circularDeps.push({
          type: 'circular',
          services: serviceNames,
          severity: 'critical',
          description: `Circular dependency detected: ${serviceNames.join(' → ')}`,
          recommendation: 'Break the cycle by introducing a message queue or event-driven architecture',
        });
        return true;
      }

      if (visited.has(serviceId)) return false;

      visited.add(serviceId);
      recStack.add(serviceId);
      path.push(serviceId);

      const dependencies = graph.get(serviceId) || new Set();
      for (const depId of dependencies) {
        if (detectCycle(depId, [...path])) {
          // Cycle detected
        }
      }

      recStack.delete(serviceId);
      return false;
    };

    validServices.forEach(service => {
      if (!visited.has(service.id)) {
        detectCycle(service.id, []);
      }
    });

    // Detect Single Points of Failure (SPOF)
    const spofIssues: DependencyIssue[] = [];
    validServices.forEach(service => {
      const dependentCount = reverseGraph.get(service.id)?.size || 0;
      if (dependentCount >= 3 && service.criticalityLevel === 'critical') {
        const dependents = Array.from(reverseGraph.get(service.id) || [])
          .map(id => validServices.find(s => s.id === id)?.name || id);

        spofIssues.push({
          type: 'spof',
          services: [service.name, ...dependents],
          severity: 'high',
          description: `${service.name} is a single point of failure (${dependentCount} services depend on it)`,
          recommendation: 'Add redundancy, implement circuit breakers, or use a service mesh',
        });
      }
    });

    // Detect deep dependency chains
    const deepChainIssues: DependencyIssue[] = [];
    const getChainDepth = (serviceId: string, depth: number = 0, visited: Set<string> = new Set()): number => {
      if (visited.has(serviceId)) return depth;
      visited.add(serviceId);

      const dependencies = graph.get(serviceId) || new Set();
      if (dependencies.size === 0) return depth;

      let maxDepth = depth;
      for (const depId of dependencies) {
        const chainDepth = getChainDepth(depId, depth + 1, new Set(visited));
        maxDepth = Math.max(maxDepth, chainDepth);
      }
      return maxDepth;
    };

    validServices.forEach(service => {
      const depth = getChainDepth(service.id);
      if (depth >= 4) {
        deepChainIssues.push({
          type: 'deep-chain',
          services: [service.name],
          severity: 'medium',
          description: `${service.name} has a dependency chain depth of ${depth}`,
          recommendation: 'Consider flattening the architecture or using caching to reduce coupling',
        });
      }
    });

    // Combine all issues
    const allIssues = [...circularDeps, ...spofIssues, ...deepChainIssues];

    // Calculate blast radius for each service
    const blastRadius = new Map<string, number>();
    const calculateBlastRadius = (serviceId: string, visited: Set<string> = new Set()): number => {
      if (visited.has(serviceId)) return 0;
      visited.add(serviceId);

      const dependents = reverseGraph.get(serviceId) || new Set();
      let radius = dependents.size;

      for (const depId of dependents) {
        radius += calculateBlastRadius(depId, visited);
      }

      return radius;
    };

    validServices.forEach(service => {
      blastRadius.set(service.id, calculateBlastRadius(service.id));
    });

    // Protocol distribution
    const protocolCounts = validServices.reduce((acc, s) => {
      acc[s.protocol] = (acc[s.protocol] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Criticality distribution
    const criticalityCounts = validServices.reduce((acc, s) => {
      acc[s.criticalityLevel] = (acc[s.criticalityLevel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalServices: validServices.length,
      totalDependencies: validServices.reduce((sum, s) => sum + s.dependsOn.length, 0),
      issues: allIssues,
      criticalIssues: allIssues.filter(i => i.severity === 'critical').length,
      highIssues: allIssues.filter(i => i.severity === 'high').length,
      blastRadius,
      protocolCounts,
      criticalityCounts,
      validServices,
    };
  }, [services]);

  // Service functions
  const addService = () => {
    if (services.length >= 10) {
      alert('Free tier limited to 10 services. Upgrade to Pro for unlimited services.');
      return;
    }
    setServices([
      ...services,
      { id: Date.now().toString(), name: '', dependsOn: [], protocol: 'http', criticalityLevel: 'medium' },
    ]);
  };

  const updateService = (id: string, field: keyof Service, value: any) => {
    setServices(services.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeService = (id: string) => {
    // Remove service and clean up dependencies
    setServices(services.filter(s => s.id !== id).map(s => ({
      ...s,
      dependsOn: s.dependsOn.filter(depId => depId !== id),
    })));
  };

  const toggleDependency = (serviceId: string, dependencyId: string) => {
    setServices(services.map(s => {
      if (s.id === serviceId) {
        const dependsOn = s.dependsOn.includes(dependencyId)
          ? s.dependsOn.filter(id => id !== dependencyId)
          : [...s.dependsOn, dependencyId];
        return { ...s, dependsOn };
      }
      return s;
    }));
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = analysis.issues.map(issue => ({
      id: `${issue.type}-${issue.services[0]?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`,
      title: issue.description,
      severity: issue.severity === 'critical' ? 'critical' as const :
                issue.severity === 'high' ? 'high' as const : 'medium' as const,
      description: `Type: ${issue.type.replace('-', ' ')}. Affected services: ${issue.services.join(', ')}`,
      recommendation: issue.recommendation,
    }));

    const recommendations = [
      ...analysis.issues.filter(i => i.severity === 'critical').map(issue => ({
        id: `fix-${issue.type}`,
        title: `Fix ${issue.type.replace('-', ' ')}`,
        priority: 'high' as const,
        description: issue.recommendation,
        effort: 'High',
      })),
      ...(analysis.highIssues > 0 ? [{
        id: 'address-high-issues',
        title: 'Address High Severity Issues',
        priority: 'high' as const,
        description: `${analysis.highIssues} high severity issue(s) found that need attention.`,
        effort: 'Medium',
      }] : []),
    ];

    // Calculate health score (inverse of issues)
    const healthScore = Math.max(0, 100 - (analysis.criticalIssues * 25) - (analysis.highIssues * 10) - (analysis.issues.length * 5));

    const assessment = await saveResult({
      name: `Microservices Dependency Analysis - ${new Date().toLocaleDateString()}`,
      score_overall: healthScore,
      scores: {
        totalServices: analysis.totalServices,
        totalDependencies: analysis.totalDependencies,
        criticalIssues: analysis.criticalIssues,
        highIssues: analysis.highIssues,
        totalIssues: analysis.issues.length,
      },
      answers: {
        services: analysis.validServices.map(s => ({
          name: s.name,
          protocol: s.protocol,
          criticality: s.criticalityLevel,
          dependencies: s.dependsOn.map(id => services.find(srv => srv.id === id)?.name || id),
          blastRadius: analysis.blastRadius.get(s.id) || 0,
        })),
      },
      gaps,
      recommendations,
      metadata: {
        protocolDistribution: analysis.protocolCounts,
        criticalityDistribution: analysis.criticalityCounts,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Services Summary
      const summaryData = [
        ['Microservices Dependency Map', ''],
        ['', ''],
        ['Overview', ''],
        ['Total Services', analysis.totalServices],
        ['Total Dependencies', analysis.totalDependencies],
        ['Critical Issues', analysis.criticalIssues],
        ['High Severity Issues', analysis.highIssues],
        ['Total Issues', analysis.issues.length],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Services
      const servicesData = analysis.validServices.map(s => ({
        'Service Name': s.name,
        'Protocol': s.protocol.toUpperCase(),
        'Criticality': s.criticalityLevel.toUpperCase(),
        'Dependencies': s.dependsOn.map(id =>
          services.find(srv => srv.id === id)?.name || id
        ).join(', ') || 'None',
        'Blast Radius': analysis.blastRadius.get(s.id) || 0,
      }));

      const ws2 = XLSX.utils.json_to_sheet(servicesData);
      ws2['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 40 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Services');

      // Sheet 3: Issues
      if (analysis.issues.length > 0) {
        const issuesData = analysis.issues.map(issue => ({
          'Type': issue.type.toUpperCase(),
          'Severity': issue.severity.toUpperCase(),
          'Services Affected': issue.services.join(', '),
          'Description': issue.description,
          'Recommendation': issue.recommendation,
        }));

        const ws3 = XLSX.utils.json_to_sheet(issuesData);
        ws3['!cols'] = [{ wch: 15 }, { wch: 12 }, { wch: 30 }, { wch: 50 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(wb, ws3, 'Issues');
      }

      // Sheet 4: Dependency Matrix
      const matrix: any[] = [['', ...analysis.validServices.map(s => s.name)]];
      analysis.validServices.forEach(service => {
        const row = [service.name];
        analysis.validServices.forEach(dep => {
          row.push(service.dependsOn.includes(dep.id) ? 'X' : '');
        });
        matrix.push(row);
      });

      const ws4 = XLSX.utils.aoa_to_sheet(matrix);
      XLSX.utils.book_append_sheet(wb, ws4, 'Dependency Matrix');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'microservices-dependency-map.xlsx');
    };
    document.head.appendChild(script);
  };

  const getSeverityColor = (severity: DependencyIssue['severity']) => {
    switch (severity) {
      case 'critical': return 'text-red-500';
      case 'high': return 'text-accent';
      case 'medium': return 'text-yellow-500';
    }
  };

  const getSeverityBgColor = (severity: DependencyIssue['severity']) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 border-red-500/30';
      case 'high': return 'bg-accent-muted border-border';
      case 'medium': return 'bg-yellow-500/10 border-yellow-500/30';
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Microservices Dependency Mapper
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Visualize service dependencies, detect circular dependencies, identify single points of failure,
            and calculate blast radius for your microservices architecture.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Services */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  Services ({analysis.validServices.length})
                </h2>
                <button
                  onClick={addService}
                  className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-sm"
                >
                  + Add Service
                </button>
              </div>

              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {services.map((service) => (
                  <div key={service.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <input
                        type="text"
                        placeholder="Service name"
                        value={service.name}
                        onChange={(e) => updateService(service.id, 'name', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground font-semibold"
                      />
                      <button
                        onClick={() => removeService(service.id)}
                        className="ml-2 text-red-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Protocol
                        </label>
                        <select
                          value={service.protocol}
                          onChange={(e) => updateService(service.id, 'protocol', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        >
                          <option value="http">HTTP/REST</option>
                          <option value="grpc">gRPC</option>
                          <option value="messaging">Messaging</option>
                          <option value="database">Database</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Criticality
                        </label>
                        <select
                          value={service.criticalityLevel}
                          onChange={(e) => updateService(service.id, 'criticalityLevel', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        >
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>
                    </div>

                    {/* Dependencies */}
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-2">
                        Depends On:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {services.filter(s => s.id !== service.id && s.name.trim()).map(dep => (
                          <button
                            key={dep.id}
                            onClick={() => toggleDependency(service.id, dep.id)}
                            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                              service.dependsOn.includes(dep.id)
                                ? 'bg-accent text-accent-foreground border-accent'
                                : 'bg-gray-100 dark:bg-background text-light-text dark:text-foreground border-gray-300 dark:border-border hover:border-accent'
                            }`}
                          >
                            {dep.name}
                          </button>
                        ))}
                        {services.filter(s => s.id !== service.id && s.name.trim()).length === 0 && (
                          <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                            Add more services to create dependencies
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {services.length >= 10 && (
                <div className="mt-3 text-sm text-accent">
                  ⚠️ Free tier limited to 10 services. Upgrade to Pro for unlimited.
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Analysis */}
          <div className="space-y-6">
            {/* Overview */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Architecture Overview
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Total Services
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {analysis.totalServices}
                  </div>
                </div>

                <div className="bg-gray-100 dark:bg-background rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Dependencies
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {analysis.totalDependencies}
                  </div>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="text-xs text-red-500 mb-1">
                    Critical Issues
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    {analysis.criticalIssues}
                  </div>
                </div>

                <div className="bg-accent-muted border border-border rounded-lg p-4">
                  <div className="text-xs text-accent mb-1">
                    High Severity
                  </div>
                  <div className="text-2xl font-bold text-accent">
                    {analysis.highIssues}
                  </div>
                </div>
              </div>
            </div>

            {/* Issues */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Issues Detected ({analysis.issues.length})
              </h2>

              {analysis.issues.length > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {analysis.issues.map((issue, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 ${getSeverityBgColor(issue.severity)}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className={`text-xs font-bold ${getSeverityColor(issue.severity)}`}>
                            {issue.severity.toUpperCase()}
                          </span>
                          <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary ml-2">
                            {issue.type.replace('-', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-light-text dark:text-foreground mb-2">
                        {issue.description}
                      </p>
                      <p className="text-xs text-light-text-secondary dark:text-foreground-secondary">
                        <span className="font-semibold">Fix:</span> {issue.recommendation}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-light-text-secondary dark:text-foreground-secondary">
                    No issues detected. Your architecture looks healthy!
                  </p>
                </div>
              )}
            </div>

            {/* Protocol Distribution */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Protocol Distribution
              </h2>

              <div className="space-y-3">
                {Object.entries(analysis.protocolCounts).map(([protocol, count]) => (
                  <div key={protocol} className="flex items-center gap-3">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary w-24 capitalize">
                      {protocol === 'http' ? 'HTTP/REST' : protocol}
                    </span>
                    <div className="flex-1 bg-gray-200 dark:bg-background rounded-full h-6 overflow-hidden">
                      <div
                        className="bg-accent h-full flex items-center justify-end px-2"
                        style={{ width: `${(count / analysis.totalServices) * 100}%` }}
                      >
                        <span className="text-xs font-semibold text-accent-foreground">{count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Blast Radius Top Services */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Highest Blast Radius
              </h2>

              <div className="space-y-2">
                {[...analysis.validServices]
                  .sort((a, b) => (analysis.blastRadius.get(b.id) || 0) - (analysis.blastRadius.get(a.id) || 0))
                  .slice(0, 5)
                  .map(service => (
                    <div key={service.id} className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-border last:border-0">
                      <span className="text-sm font-semibold text-light-text dark:text-foreground">
                        {service.name}
                      </span>
                      <span className="text-sm text-accent font-bold">
                        {analysis.blastRadius.get(service.id) || 0} services
                      </span>
                    </div>
                  ))}
              </div>

              <p className="text-xs text-light-text-secondary dark:text-foreground-secondary mt-3">
                Blast radius = number of services that would be affected if this service fails
              </p>
            </div>

            {/* Export & Save */}
            <div className="space-y-3">
              <button
                onClick={exportToExcel}
                className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Export Dependency Map to Excel
              </button>
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Unlimited services</li>
                <li>✓ Interactive graph with filters</li>
                <li>✓ Blast radius calculator</li>
                <li>✓ Single point of failure analysis</li>
                <li>✓ Excel export with dependency matrix</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Auto-discovery from K8s/ECS</li>
                <li>✓ Performance bottleneck detection</li>
                <li>✓ Cost allocation by service</li>
                <li>✓ Export to C4/ArchiMate diagrams</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
